import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { AppSettingsAlt, Delete, AddCircleOutline, Save, Close } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemText,
  Box,
  IconButton,
  Divider,
  Typography,
  TextField,
  Button,
  ButtonGroup
} from '@mui/material';
import { Card, Loader } from '../../components';
import { IDropdownResponse, IAuthUserMFAOptions } from '../../models';
import {
  getUserMfaTypes,
  getUserMfaOptions,
  deleteMFAOption,
  validateNewMFA,
  sendAuthyText,
  sendSMSText
} from '../../fetch';
import { MFASetupOptions } from '../login/mfa-setup-options';
import { LoadingButton } from '@mui/lab';
import { formatDate } from '../../helpers';

export const MFASettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeMFA, setActiveMFA] = useState<{
    [id: number]: {
      active?: boolean;
      code?: string;
    };
  }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSendingText, setIsSendingText] = useState<boolean>(false);
  const { isLoading: isLoadingTypes, data: mfaTypes } = useQuery<IDropdownResponse[], Error>(
    ['mfa-types'],
    () => getUserMfaTypes()
  );
  const {
    isLoading: isLoadingOptions,
    data: existingMfaOptions,
    refetch: refetchExistingMfaOptions
  } = useQuery<IAuthUserMFAOptions[], Error>(['mfa-options'], () => getUserMfaOptions());

  useEffect(() => {
    if (existingMfaOptions) {
      setActiveMFA(
        existingMfaOptions?.reduce((prev, curr) => {
          return {
            ...prev,
            [curr.twoFactorAuthenticationId]: {
              active: false,
              code: ''
            }
          };
        }, {})
      );
    }
  }, [existingMfaOptions]);
  return (
    <>
      {(isLoadingTypes || isLoadingOptions) && <Loader position="centered" />}
      {!isLoadingTypes && !isLoadingOptions && (
        <Card title="MFA Settings" icon={<AppSettingsAlt />}>
          <Typography sx={{ fontWeight: 'bold' }}>Configured MFA Options</Typography>
          {existingMfaOptions?.length === 0 && (
            <Typography sx={{ marginTop: '1rem' }} align="center">
              No MFA options configured.
            </Typography>
          )}
          <List>
            {existingMfaOptions?.map(option => {
              return (
                <ListItem
                  key={`${option.twoFactorAuthenticationId}`}
                  secondaryAction={
                    <Box display="flex">
                      <Button
                        color="error"
                        disabled={
                          isSaving ||
                          isSendingText ||
                          activeMFA?.[option.twoFactorAuthenticationId]?.active
                        }
                        startIcon={<Delete />}
                        onClick={async () => {
                          const result = window.confirm(
                            'Are you sure you want to delete this MFA option?'
                          );
                          if (result) {
                            try {
                              await deleteMFAOption(option.twoFactorAuthenticationId);
                              enqueueSnackbar('MFA option deleted!', {
                                variant: 'success'
                              });
                              refetchExistingMfaOptions();
                            } catch (error) {
                              enqueueSnackbar(
                                error?.Detail ?? 'Error deleting MFA option, please try again.',
                                {
                                  variant: 'error'
                                }
                              );
                            }
                          }
                        }}
                      >
                        Delete
                      </Button>
                      {!option.isActive && (
                        <Button
                          disabled={
                            isSaving ||
                            isSendingText ||
                            activeMFA?.[option.twoFactorAuthenticationId]?.active
                          }
                          color="secondary"
                          startIcon={<AddCircleOutline />}
                          onClick={async () => {
                            setActiveMFA({
                              ...activeMFA,
                              [option.twoFactorAuthenticationId]: {
                                active: true,
                                code: ''
                              }
                            });
                            if (option?.type === 'Text' || option?.type === 'Authy') {
                              setIsSendingText(true);
                              try {
                                option?.type === 'Authy'
                                  ? await sendAuthyText(option.twoFactorAuthenticationId)
                                  : await sendSMSText(option.twoFactorAuthenticationId);
                                enqueueSnackbar('Text message sent!', {
                                  variant: 'success'
                                });
                              } catch (error) {
                                enqueueSnackbar(
                                  error?.Detail ?? 'Error sending, please try again.',
                                  {
                                    variant: 'error'
                                  }
                                );
                              } finally {
                                setIsSendingText(false);
                              }
                            }
                          }}
                        >
                          Activate
                        </Button>
                      )}
                    </Box>
                  }
                >
                  <Box display="flex" flexWrap={'wrap'} maxWidth={'50%'}>
                    <ListItemText
                      sx={{ flex: 'none' }}
                      primary={`${option.typeDisplayName}${
                        option.phoneNumber || option.displayName
                          ? ` - ${option.phoneNumber || option.displayName}`
                          : ''
                      }`}
                      secondary={
                        <>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Active: {`${option.isActive}`}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Created On: {formatDate(option.createdOn)}
                          </Typography>
                        </>
                      }
                    />
                    {activeMFA?.[option.twoFactorAuthenticationId]?.active && (
                      <Box display="flex" gap={0.5} alignItems="center" flexWrap={'wrap'}>
                        <TextField
                          label="Code"
                          required
                          type="text"
                          disabled={isSendingText}
                          size="small"
                          helperText={
                            option.type === 'App'
                              ? 'Enter the code in your authenticator app'
                              : 'Enter the code sent to you'
                          }
                          onChange={e => {
                            setActiveMFA({
                              [option.twoFactorAuthenticationId]: {
                                ...activeMFA?.[option.twoFactorAuthenticationId],
                                code: e.target.value
                              }
                            });
                          }}
                          value={activeMFA?.[option.twoFactorAuthenticationId]?.code ?? ''}
                        />
                        <ButtonGroup sx={{ marginLeft: '1rem' }}>
                          <LoadingButton
                            loading={isSaving}
                            loadingPosition="start"
                            startIcon={<Save />}
                            color="primary"
                            variant="text"
                            disabled={
                              !activeMFA?.[option.twoFactorAuthenticationId]?.code || isSaving
                            }
                            onClick={async () => {
                              try {
                                setIsSaving(true);
                                await validateNewMFA(
                                  option.twoFactorAuthenticationId,
                                  activeMFA?.[option.twoFactorAuthenticationId]?.code
                                );
                                enqueueSnackbar('MFA option activated!', {
                                  variant: 'success'
                                });
                                refetchExistingMfaOptions();
                                setActiveMFA({
                                  ...activeMFA,
                                  [option.twoFactorAuthenticationId]: {
                                    active: false,
                                    code: ''
                                  }
                                });
                              } catch (error) {
                                enqueueSnackbar(
                                  error?.Detail ?? 'Error activating MFA option, please try again.',
                                  {
                                    variant: 'error'
                                  }
                                );
                              } finally {
                                setIsSaving(false);
                              }
                            }}
                          >
                            Save
                          </LoadingButton>
                          <IconButton
                            color="inherit"
                            title="Cancel"
                            disabled={isSaving}
                            onClick={() => {
                              setActiveMFA({
                                ...activeMFA,
                                [option.twoFactorAuthenticationId]: {
                                  active: false,
                                  code: ''
                                }
                              });
                            }}
                          >
                            <Close />
                          </IconButton>
                        </ButtonGroup>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
          <Divider />
          <Box mt={1}>
            <Typography sx={{ fontWeight: 'bold' }}>Setup a new MFA option</Typography>
            <MFASetupOptions
              mfaTypes={mfaTypes}
              finalButtonText="Save"
              showAlertMessage={false}
              handleValidateNewMFA={() => {
                refetchExistingMfaOptions();
              }}
            />
          </Box>
        </Card>
      )}
    </>
  );
};
